import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Skiper72: Horizontal Text Reveal
 * Inspired by skiper-ui.com/v1/skiper72 (SkiperTextRevealH)
 *
 * Staggered word-by-word horizontal reveal scrubbed to scroll:
 * each word smoothly translates along the X-axis from positive offset
 * and fades in with subtle kinetic skew, settling into normal typographic alignment.
 */

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function splitIntoWords(el: HTMLElement): HTMLElement[] {
  const text = el.textContent?.trim() || '';
  if (!text) return [];

  // Preserve accessible reading text for screen readers
  el.setAttribute('aria-label', text);
  el.textContent = '';

  const words = text.split(/\s+/);
  const spans: HTMLElement[] = [];

  words.forEach((w) => {
    const span = document.createElement('span');
    span.className = 'skiper72-word';
    span.setAttribute('aria-hidden', 'true');
    span.textContent = w;
    el.appendChild(span);
    spans.push(span);
  });

  return spans;
}

export function initSkiper72(): void {
  const rows = [...document.querySelectorAll<HTMLElement>('.svc-row[data-skiper72]')];
  if (!rows.length) return;

  const reduced = reduceMotion();

  rows.forEach((row) => {
    const num = row.querySelector<HTMLElement>('.svc-row-n');
    const ic = row.querySelector<HTMLElement>('.svc-row-ic');
    const nameEl = row.querySelector<HTMLElement>('.svc-row-name');
    const tagEl = row.querySelector<HTMLElement>('.svc-row-tag');
    const arr = row.querySelector<HTMLElement>('.svc-row-arrow');

    const nameWords = nameEl ? splitIntoWords(nameEl) : [];
    const tagWords = tagEl ? splitIntoWords(tagEl) : [];
    const allWords = [...nameWords, ...tagWords];

    if (reduced) {
      if (num) gsap.set(num, { opacity: 1, x: 0 });
      if (ic) gsap.set(ic, { opacity: 1, x: 0 });
      if (arr) gsap.set(arr, { opacity: 1 });
      if (allWords.length) gsap.set(allWords, { opacity: 1, x: 0, skewX: 0 });
      return;
    }

    // Scroll-scrubbed timeline: speed directly tracks scroll speed (scrub: true)
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: row,
        start: 'top 85%',
        end: 'top 18%',
        scrub: true, // speed strictly depends upon scroll speed
        invalidateOnRefresh: true,
      },
    });

    // 1. Chrome: Number and Icon anchor the row on the left
    const baseEls = [num, ic].filter(Boolean) as HTMLElement[];
    if (baseEls.length) {
      tl.fromTo(
        baseEls,
        { opacity: 0, x: 30 },
        { opacity: 1, x: 0, ease: 'power2.out', duration: 0.25 },
        0
      );
    }

    // 2. Every word starts from the right edge of the screen and moves to sit on the left
    if (allWords.length) {
      const rowLeft = row.getBoundingClientRect().left;
      const wordOffsets = allWords.map((w) => w.getBoundingClientRect().left - rowLeft);

      allWords.forEach((word, idx) => {
        const offsetFromRow = wordOffsets[idx];
        const startX = () => {
          const naturalLeft = row.getBoundingClientRect().left + offsetFromRow;
          return Math.max(window.innerWidth - naturalLeft + 40, window.innerWidth * 0.8);
        };

        const startTime = 0.04 + idx * 0.07;
        tl.fromTo(
          word,
          {
            x: startX,
            opacity: 0,
            skewX: -8,
          },
          {
            x: 0,
            opacity: 1,
            skewX: 0,
            ease: 'power2.out',
            duration: 0.55,
          },
          startTime
        );
      });
    }

    // 3. Arrow: fades in as the final punctuation mark when the line settles
    if (arr) {
      tl.fromTo(
        arr,
        { opacity: 0 },
        { opacity: 1, ease: 'sine.out', duration: 0.2 },
        '>-0.1'
      );
    }
  });

  ScrollTrigger.refresh();
}
